import { useState } from 'react';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { records } from '../../lib/api.ts';
import { money, parseMoney } from '../../lib/format.ts';
import { ManageLayout, useAction, useSave } from './common.tsx';

interface Group {
  id: string;
  name: string;
  min_select: number;
  max_select: number;
}
interface Option {
  id: string;
  group: string;
  name: string;
  price_delta: number;
  sequence: number;
  active: boolean;
}
interface Data {
  modifier_groups: Group[];
  modifiers: Option[];
  products: { id: string; name: string }[];
  product_modifier_groups: { product: string; group: string }[];
}

/** "Pick 1, required" / "Pick up to 3" / "Optional, any number". */
export function ruleText(g: Pick<Group, 'min_select' | 'max_select'>): string {
  if (g.min_select > 0 && g.min_select === g.max_select) return t('options_admin.rule_exactly', { n: g.min_select });
  if (g.min_select > 0) return t('options_admin.rule_range', { min: g.min_select, max: g.max_select || '∞' });
  if (g.max_select > 0) return t('options_admin.rule_up_to', { n: g.max_select });
  return t('options_admin.rule_any');
}

export function Options() {
  const { data, reload } = useAction<Data>('products.catalog', { include_inactive: true });
  const [editing, setEditing] = useState<Group | 'new' | null>(null);

  return (
    <ManageLayout
      title={t('manage.options')}
      subtitle={t('options_admin.subtitle')}
      actions={
        <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
          {t('options_admin.add')}
        </Button>
      }
    >
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : data.modifier_groups.length === 0 ? (
        <EmptyState icon="list" title={t('options_admin.empty')} body={t('options_admin.empty_body')} />
      ) : (
        <div className="card-grid">
          {data.modifier_groups.map((g) => {
            const opts = data.modifiers.filter((m) => m.group === g.id).sort((a, b) => a.sequence - b.sequence);
            const used = data.product_modifier_groups.filter((l) => l.group === g.id).length;
            return (
              <button key={g.id} className="card option-card" onClick={() => setEditing(g)}>
                <span className="card-head">
                  <strong>{g.name}</strong>
                  <span className="muted">{ruleText(g)}</span>
                </span>
                <span className="chips">
                  {opts.map((o) => (
                    <span key={o.id} className={`tag${o.active ? '' : ' archived'}`}>
                      {o.name}
                      {o.price_delta ? ` ${money(o.price_delta, { sign: true })}` : ''}
                    </span>
                  ))}
                </span>
                <span className="muted">{used ? tn('options_admin.used_by', used) : t('options_admin.not_used')}</span>
              </button>
            );
          })}
        </div>
      )}
      {editing && data ? (
        <GroupDialog
          group={editing === 'new' ? null : editing}
          options={editing === 'new' ? [] : data.modifiers.filter((m) => m.group === editing.id).sort((a, b) => a.sequence - b.sequence)}
          usedBy={editing === 'new' ? 0 : data.product_modifier_groups.filter((l) => l.group === editing.id).length}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      ) : null}
    </ManageLayout>
  );
}

interface Draft {
  id?: string;
  name: string;
  price: string;
  active: boolean;
}

function GroupDialog({
  group,
  options,
  usedBy,
  onClose,
  onSaved,
}: {
  group: Group | null;
  options: Option[];
  usedBy: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { busy, run } = useSave();
  const [name, setName] = useState(group?.name ?? '');
  const [required, setRequired] = useState((group?.min_select ?? 0) > 0);
  const [max, setMax] = useState(group?.max_select ?? 1);
  const [drafts, setDrafts] = useState<Draft[]>(
    options.length
      ? options.map((o) => ({ id: o.id, name: o.name, price: o.price_delta ? String(o.price_delta / 100) : '', active: o.active }))
      : [{ name: '', price: '', active: true }],
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tried, setTried] = useState(false);

  const filled = drafts.filter((d) => d.name.trim());
  const badPrice = drafts.some((d) => d.name.trim() && d.price.trim() && parseMoney(d.price.replace('-', '')) === null);
  const problem = !name.trim()
    ? t('options_admin.need_name')
    : filled.length === 0
      ? t('options_admin.need_option')
      : badPrice
        ? t('menu.price_invalid')
        : required && max > 0 && filled.filter((d) => d.active).length < 1
          ? t('options_admin.need_option')
          : null;

  const patch = (i: number, p: Partial<Draft>) => setDrafts((ds) => ds.map((d, j) => (j === i ? { ...d, ...p } : d)));
  const delta = (text: string) => {
    const neg = text.trim().startsWith('-');
    const v = parseMoney(text.replace('-', '')) ?? 0;
    return neg ? -v : v;
  };

  const save = () => {
    setTried(true);
    if (problem) return;
    void run(async () => {
      // Keep a stricter minimum set earlier (e.g. "choose 2"), as long as it still fits the maximum.
      const min = required ? Math.max(1, group?.min_select ?? 1) : 0;
      const values = { name: name.trim(), min_select: max > 0 ? Math.min(min, max) : min, max_select: max };
      const g = group ? await records.update<Group>('product.modifier_group', group.id, values) : await records.create<Group>('product.modifier_group', values);
      for (const o of options) if (!drafts.some((d) => d.id === o.id && d.name.trim())) await records.remove('product.modifier', o.id);
      for (const [i, d] of drafts.entries()) {
        if (!d.name.trim()) continue;
        const v = { name: d.name.trim(), price_delta: delta(d.price), sequence: (i + 1) * 10, active: d.active };
        if (d.id) await records.update('product.modifier', d.id, v);
        else await records.create('product.modifier', { ...v, group: g.id });
      }
    }, t('menu.saved', { name: name.trim() })).then((ok) => ok && onSaved());
  };

  const remove = () =>
    void run(() => records.remove('product.modifier_group', group!.id), t('options_admin.deleted', { name: group!.name })).then((ok) => ok && onSaved());

  if (confirmDelete && group) {
    return (
      <Confirm
        title={t('options_admin.delete_title', { name: group.name })}
        body={usedBy ? tn('options_admin.delete_body_used', usedBy) : t('options_admin.delete_body')}
        confirm={t('options_admin.delete')}
        danger
        busy={busy}
        onConfirm={remove}
        onClose={() => setConfirmDelete(false)}
      />
    );
  }

  return (
    <Dialog
      title={group ? group.name : t('options_admin.add')}
      onClose={onClose}
      footer={
        <>
          {group ? (
            <Button variant="ghost" icon="trash" onClick={() => setConfirmDelete(true)} disabled={busy}>
              {t('options_admin.delete')}
            </Button>
          ) : (
            <span />
          )}
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={t('options_admin.name')} hint={t('options_admin.name_hint')}>
        <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-autofocus />
      </Field>
      <div className="form-stack">
        <fieldset className="option-group">
          <legend>{t('options_admin.must_choose')}</legend>
          <div className="segmented" role="radiogroup">
            <button role="radio" aria-checked={!required} className={!required ? 'active' : ''} onClick={() => setRequired(false)}>
              {t('options.optional')}
            </button>
            <button role="radio" aria-checked={required} className={required ? 'active' : ''} onClick={() => setRequired(true)}>
              {t('options.required')}
            </button>
          </div>
        </fieldset>
        <fieldset className="option-group">
          <legend>{t('options_admin.how_many')}</legend>
          <div className="segmented" role="radiogroup">
            {[1, 2, 3, 0].map((n) => (
              <button key={n} role="radio" aria-checked={max === n} className={max === n ? 'active' : ''} onClick={() => setMax(n)}>
                {n === 0 ? t('options_admin.any') : n === 1 ? t('options_admin.only_one') : t('options_admin.up_to_n', { n })}
              </button>
            ))}
          </div>
        </fieldset>
      </div>
      <p className="muted">{ruleText({ min_select: required ? 1 : 0, max_select: max })}</p>

      <fieldset className="option-group">
        <legend>{t('options_admin.choices')}</legend>
        <div className="edit-rows">
          {drafts.map((d, i) => (
            <div key={d.id ?? `n${i}`} className={`edit-row${d.active ? '' : ' archived'}`}>
              <input
                className="input"
                placeholder={t('options_admin.choice_name')}
                aria-label={t('options_admin.choice_name')}
                value={d.name}
                maxLength={60}
                onChange={(e) => patch(i, { name: e.target.value })}
              />
              <input
                className="input price-input"
                placeholder={t('options_admin.extra_price')}
                aria-label={t('options_admin.extra_price')}
                inputMode="decimal"
                value={d.price}
                onChange={(e) => patch(i, { price: e.target.value })}
              />
              <IconButton
                icon={d.active ? 'eye' : 'eyeOff'}
                label={d.active ? t('options_admin.hide', { name: d.name || '…' }) : t('options_admin.show', { name: d.name || '…' })}
                onClick={() => patch(i, { active: !d.active })}
              />
              <IconButton icon="trash" label={t('options_admin.remove_choice')} onClick={() => setDrafts((ds) => ds.filter((_, j) => j !== i))} />
            </div>
          ))}
        </div>
        <Button variant="ghost" icon="plus" onClick={() => setDrafts((ds) => [...ds, { name: '', price: '', active: true }])}>
          {t('options_admin.add_choice')}
        </Button>
      </fieldset>
      {tried && problem ? <p className="form-error" role="alert">{problem}</p> : null}
    </Dialog>
  );
}
